import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { IEpisode } from '@packages/global';
import ImageUpload from '../common/ImageUpload';

export interface EpisodeFormData {
  title: string;
  description: string;
  thumbnailUrl: string;
}

interface EpisodeFormDrawerProps {
  isOpen: boolean;
  editingEpisode: IEpisode | null;
  onSubmit: (data: EpisodeFormData) => Promise<void>;
  onClose: () => void;
}

export default function EpisodeFormDrawer({
  isOpen,
  editingEpisode,
  onSubmit,
  onClose
}: EpisodeFormDrawerProps) {
  const { t } = useTranslation(['project', 'common']);
  const [formData, setFormData] = useState<EpisodeFormData>({
    title: '',
    description: '',
    thumbnailUrl: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingEpisode) {
      setFormData({
        title: editingEpisode.title,
        description: editingEpisode.description || '',
        thumbnailUrl: editingEpisode.thumbnailUrl || ''
      });
    } else {
      setFormData({
        title: '',
        description: '',
        thumbnailUrl: ''
      });
    }
  }, [editingEpisode, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-bg-surface rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border-subtle">
          <h2 className="text-xl font-semibold text-gray-50">
            {t('project:episodeForm.editTitle')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300 transition-colors cursor-pointer">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('project:episodeForm.titleLabel')}</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="block w-full px-3 py-2 border border-border-subtle rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm bg-bg-form text-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('project:episodeForm.descriptionLabel')}</label>
              <textarea
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="block w-full px-3 py-2 border border-border-subtle rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm bg-bg-form text-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('project:episodeForm.thumbnail')}</label>
              {formData.thumbnailUrl ? (
                <div className="relative w-32">
                  <img
                    src={formData.thumbnailUrl}
                    alt={t('project:episodeForm.thumbnail')}
                    className="w-full h-48 object-cover rounded border border-border-subtle"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, thumbnailUrl: '' })}
                    className="absolute top-2 right-2 p-1 bg-error text-white rounded-full hover:bg-error/80 transition-colors cursor-pointer"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <ImageUpload
                  className='w-32 h-48'
                  onUploadSuccess={(url) => setFormData({ ...formData, thumbnailUrl: url })}
                />
              )}
            </div>
          </form>
        </div>

        <div className="p-6 border-t border-border-subtle">
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.title.trim()}
              className="flex-1 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 transition-colors cursor-pointer"
            >
              {loading ? t('project:episodeForm.saving') : t('common:save')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 inline-flex justify-center py-2 px-4 border border-border-subtle shadow-sm text-sm font-medium rounded-md text-gray-300 bg-bg-elevated hover:bg-bg-surface focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors cursor-pointer"
            >
              {t('common:cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
